import { eventLocationKey } from '@app/src/components/event-management/constants/eventModuleKeys';
import AuthStore from '@app/src/common/store/AuthStore';
import { UserPermissions } from '@app/src/common/model/AuthModel';
import LocationRepository from '@app/src/components/event-management/repository/LocationRepository';
import AppStore from '@app/src/common/store/AppStore';
import EventsRepository from '@app/src/components/event-management/repository/EventsRepository';
import ContactsRepository from '@app/src/components/event-management/repository/ContactsRepository';
import DictionaryRepository from '@app/src/components/event-management/repository/DictionaryRepository';
import LocaleStore from '@app/src/common/store/LocaleStore';
import BookingRepository from '@app/src/components/booking-management/repository/BookingRepository';
import { ISelectOption } from '@app/src/common/model/FormField';
import { IDictionary } from '@app/src/common/model/FormConfiguration';
import UserManagementRepository from '@app/src/common/repository/UserManagementRepository';
import ResourceRepository from '@app/src/components/user-management/repository/ResourceRepository';
import { CONTACTS_DICTIONARY } from '@app/src/common/utils/contactsDictionary';

export * from '@app/src/common/utils/contactsDictionary';

/** The subset of IEventContactName / IEventContact a contact option needs. */
interface IContactOptionSource {
  id: string;
  email: string;
  fullName: string | null;
}

export function initDictionaries(
  names: string[],
  isForm: boolean = false,
  loadParams?: Record<string, any>,
  onPartialLoad?: (partial: IDictionary) => void
) {
  const promises = names
    .map(name => promiseByName(name, isForm, loadParams))
    .filter(item => item)
    .map(promise =>
      onPartialLoad
        ? promise!.then(result => {
            onPartialLoad(result as IDictionary);
            return result;
          })
        : promise
    );
  return Promise.all(promises).then(results =>
    results.reduce((obj, item) => Object.assign(obj, item), {})
  );
}

function promiseByName(
  name: string,
  isForm: boolean,
  loadParams?: Record<string, any>
) {
  // a source name may carry static request params: 'companyResources?onlyChild=true';
  // the result stays under the full name, so it does not clash with the plain one
  const [baseName, query] = name.split('?');
  const staticParams = query
    ? Object.fromEntries(new URLSearchParams(query))
    : undefined;
  if (staticParams) {
    loadParams = { ...staticParams, ...loadParams };
  }
  switch (baseName) {
    case 'locations': {
      const isEventLocation = AppStore.configurablePages.get(eventLocationKey);
      const canFetchLocations = AuthStore.userPermissions
        .slice()
        .includes(UserPermissions.eventmanagement_view_location_company);
      if (isEventLocation && canFetchLocations) {
        let params;
        if (isForm) {
          params = { toEditVisits: true } as any;
        }
        return LocationRepository.getLocations(params).then(({ items }) => ({
          locations: items.map(mapToFilterOption)
        }));
      } else {
        return Promise.resolve({});
      }
    }
    case 'visitorTypeNames': {
      return EventsRepository.getVisitorTypes().then(({ items }) => ({
        visitorTypeNames: items.map(item => ({
          value: item.name,
          label: item.name
        }))
      }));
    }
    case 'visitTypes': {
      return EventsRepository.getVisitTypes({ active: true }).then(
        ({ items }) => ({
          visitTypes: items.map(item => ({
            value: item.shortName,
            label: `${item.shortName} - ${item.name}`
          }))
        })
      );
    }
    case 'visitTypesSelect': {
      return EventsRepository.getVisitTypes({ active: true }).then(
        ({ items }) => ({
          visitTypes: items.map(item => ({
            value: item.id,
            label: `${item.shortName} - ${item.name}`
          }))
        })
      );
    }
    case 'contacts':
    case 'contactsFull':
    case 'contactsExtended': {
      // 'contacts' is the light /names payload; the other two carry the full
      // contact record, with assets for 'contactsExtended'
      const request: Promise<IContactOptionSource[]> =
        name === CONTACTS_DICTIONARY
          ? ContactsRepository.getContactNames(loadParams)
          : ContactsRepository.getContacts({
              ...(loadParams ?? {}),
              includeAssets: name === 'contactsExtended'
            }).then(({ items }) => items);

      return request.then(items => ({
        [name]: items.map(item => ({
          value: item.id,
          label: item.fullName || item.email,
          option: item
        }))
      }));
    }
    case 'companies': {
      const params: any = { ...(loadParams ?? {}), includeDeleted: !isForm };
      return EventsRepository.getVisitorCompanies(params).then(({ items }) => ({
        companies: items.map(item => ({
          value: item.id,
          label: item.name,
          option: item
        }))
      }));
    }
    case 'rooms': {
      return BookingRepository.getRoomNames().then(items => ({
        rooms: items.map(mapToFilterOption)
      }));
    }
    case 'bookingLocations': {
      return BookingRepository.getLocations().then(({ data }) => ({
        bookingLocations: data.items.map(mapToFilterOption)
      }));
    }
    case 'tenants': {
      const canFetchTenants = AuthStore.hasPermission(
        UserPermissions.access_all_tenant_entities
      );
      if (canFetchTenants) {
        return UserManagementRepository.getCompanyNames().then(({ data }) => ({
          tenants: data.map(mapToFilterOption)
        }));
      } else {
        return Promise.resolve({});
      }
    }
    case 'companyResources': {
      const { companyId, ...params } = loadParams ?? {};
      return ResourceRepository.getResourceNames(
        companyId ?? AuthStore.companyId,
        params
      ).then(data => ({
        [name]: data.map(item => ({
          value: item.id,
          label: item.resource,
          option: item
        }))
      }));
    }
    default: {
      if (baseName.startsWith('dictionary')) {
        const [_, key] = baseName.split(':');
        // the form looks the options up by the source name without 'dictionary:'
        const [__, resultKey] = name.split(':');
        return DictionaryRepository.getDictionaryItemsByKey(key, {
          includeDeleted: true,
          languageCode: LocaleStore.locale,
          ...staticParams
        }).then(items => ({
          [resultKey]: items.map(item => ({
            value: item.key,
            label: item.translations[0]?.translation || item.key
          }))
        }));
      } else {
        return Promise.resolve({});
      }
    }
  }
}

export function reloadDictionary(name: string, params?: Record<string, any>) {
  return promiseByName(name, true, params).then(result => result || {});
}

export const mapToFilterOption = ({
  id,
  name
}: {
  id: string;
  name: string;
}): ISelectOption => ({
  value: id,
  label: name
});
